import React, { useState, useEffect, useCallback } from "react";

interface ESTemplate {
  id: number;
  user_id: number;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
}

interface CreateESTemplateRequest {
  title: string;
  content: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

const ESTemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<ESTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingTemplate, setEditingTemplate] = useState<ESTemplate | null>(
    null
  );
  const [formData, setFormData] = useState<CreateESTemplateRequest>({
    title: "",
    content: "",
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5173";

  // ESテンプレート一覧を取得
  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/es-templates`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data: ApiResponse<{ templates: ESTemplate[] }> =
          await response.json();
        if (data.success && data.data?.templates) {
          setTemplates(data.data.templates);
        } else {
          setTemplates([]);
        }
      } else {
        setError("ESテンプレート一覧の取得に失敗しました");
      }
    } catch (error) {
      setError("ESテンプレート一覧の取得中にエラーが発生しました");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // フォームをリセット
  const resetForm = () => {
    setFormData({ title: "", content: "" });
    setEditingTemplate(null);
    setShowForm(false);
  };

  // ESテンプレート追加/編集
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.content.trim()) {
      setError("タイトルと内容は必須です");
      return;
    }

    try {
      const url = editingTemplate
        ? `${API_BASE_URL}/api/es-templates/${editingTemplate.id}`
        : `${API_BASE_URL}/api/es-templates`;

      const method = editingTemplate ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        resetForm();
        fetchTemplates();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(
          errorData.message ||
            `ESテンプレートの${editingTemplate ? "更新" : "登録"}に失敗しました`
        );
      }
    } catch (error) {
      setError(
        `ESテンプレートの${editingTemplate ? "更新" : "登録"}中にエラーが発生しました`
      );
    }
  };

  // 編集ボタン
  const handleEdit = (template: ESTemplate) => {
    setEditingTemplate(template);
    setFormData({ title: template.title, content: template.content });
    setShowForm(true);
  };

  // 削除
  const handleDelete = async (id: number) => {
    if (!window.confirm("このESテンプレートを削除しますか?")) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/es-templates/${id}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        fetchTemplates();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "ESテンプレートの削除に失敗しました");
      }
    } catch (error) {
      setError("ESテンプレートの削除中にエラーが発生しました");
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-8 py-8">
        <p>読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-8 py-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">ESテンプレート</h2>
        <button
          onClick={() => setShowForm(true)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
        >
          新規作成
        </button>
      </div>

      {/* フォーム */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">
              {editingTemplate
                ? "ESテンプレート編集"
                : "ESテンプレート新規作成"}
            </h3>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  タイトル *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="例: 自己PR"
                />
              </div>

              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    内容 *
                  </label>
                  <span
                    className={`text-xs ${
                      formData.content.length > 4500
                        ? "text-red-600 font-semibold"
                        : formData.content.length > 4000
                          ? "text-orange-600"
                          : "text-gray-500"
                    }`}
                  >
                    {formData.content.length} 文字
                  </span>
                </div>
                <textarea
                  value={formData.content}
                  onChange={(e) =>
                    setFormData({ ...formData, content: e.target.value })
                  }
                  maxLength={5000}
                  rows={15}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="ESテンプレートの内容を入力してください"
                />
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                >
                  {editingTemplate ? "更新" : "作成"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* テンプレート一覧 */}
      {templates.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>ESテンプレートがまだありません</p>
          <p className="text-sm mt-2">「新規作成」ボタンから作成してください</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <div
              key={template.id}
              className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow flex flex-col"
            >
              <h3 className="font-bold text-lg text-gray-800 mb-2">
                {template.title}
              </h3>
              <div className="text-sm text-gray-600 mb-2 whitespace-pre-wrap overflow-y-auto max-h-60 flex-grow">
                {template.content}
              </div>
              <div className="text-xs text-gray-400 mb-3">
                {template.content.length} 文字 | 更新:{" "}
                {new Date(template.updated_at).toLocaleDateString()}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(template)}
                  className="flex-1 px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition-colors text-sm"
                >
                  編集
                </button>
                <button
                  onClick={() => handleDelete(template.id)}
                  className="flex-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors text-sm"
                >
                  削除
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ESTemplatesPage;
